import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  CALISMA_ETIKETI,
  gorevAc,
  gorevIptal,
  gorevTipleri,
  gunlukGorevler,
  type GorevOzeti,
  type Koordinat,
} from '../api/gorev';
import type { Kimlik } from '../api/tipler';
import { BirimSecici } from '../bilesenler/BirimSecici';
import { Diyalog, GerekceDiyalogu } from '../bilesenler/Diyalog';
import { GorevDurumRozeti } from '../bilesenler/GorevParcalari';
import { HaritaNoktasi } from '../bilesenler/HaritaNoktasi';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { CokSatir, Dugme, HataKutusu, Metin, Sekme } from '../bilesenler/temel';
import { bugun } from '../bicim/tarih';
import { yetkiKullan, yetkilerHazirKullan } from '../yetki/yetki';
import { GorevRaporlari } from './GorevRaporlari';
import { GorevSablonlari, GorevTipleri } from './GorevTipleri';

/**
 * GÖREVLER.
 *
 * DEVAM BEYANI UÇLARI (`kayit/*`) BURADA YOK — sözleşme gereği PWA-only.
 * Panelden kayıt başlatılabilseydi "buton beyanı tek kaynak" kuralı delinir,
 * beyanın kim tarafından yapıldığı belirsizleşirdi.
 *
 * ALTI YETKİ SEVİYESİ VAR; okuma ve yazma AYRI değişkenlerde. `GOREV_OLUSTUR`
 * görev açar ama şablon listesini GÖREMEZ (`GET /sablon` o kodu kabul
 * etmiyor) — sekme ona kapalı.
 */

type SekmeKodu = 'gunluk' | 'tip' | 'sablon' | 'rapor';

export function Gorev() {
  const yetkilerHazir = yetkilerHazirKullan();

  const gorevGorebilir = yetkiKullan(
    'PERSONEL_GORUNTULE',
    'GOREV_OLUSTUR',
    'GOREV_TIPI_YONET',
    'MESAI_KAYDI_BAS',
  );
  const tipGorebilir = yetkiKullan('PERSONEL_GORUNTULE', 'GOREV_TIPI_YONET', 'GOREV_OLUSTUR');
  // `GET /sablon` `GOREV_OLUSTUR` KABUL ETMİYOR — tip listesinden dar.
  const sablonGorebilir = yetkiKullan('PERSONEL_GORUNTULE', 'GOREV_TIPI_YONET');
  const raporGorebilir = yetkiKullan('GOREV_TIPI_YONET', 'RAPOR_GORUNTULE');

  const [sekmeSecimi, sekmeSecimiYaz] = useState<SekmeKodu | null>(null);
  const sekme: SekmeKodu = sekmeSecimi ?? (gorevGorebilir ? 'gunluk' : 'rapor');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Görevler</h1>
        <p className="mt-1 text-sm text-slate-600">
          Görev tipi bir TAHMİNLE başlar; sistem 10 kayıttan sonra gerçek ortalamayı önerir.
          Kalibrasyon otomatik uygulanmaz — sistem önerir, müdür kabul eder.
        </p>
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        {gorevGorebilir && (
          <Sekme
            etkin={sekme === 'gunluk'}
            tikla={() => sekmeSecimiYaz('gunluk')}
            etiket="Günlük görevler"
          />
        )}
        {tipGorebilir && (
          <Sekme
            etkin={sekme === 'tip'}
            tikla={() => sekmeSecimiYaz('tip')}
            etiket="Görev tipleri"
          />
        )}
        {sablonGorebilir && (
          <Sekme
            etkin={sekme === 'sablon'}
            tikla={() => sekmeSecimiYaz('sablon')}
            etiket="Şablonlar"
          />
        )}
        {raporGorebilir && (
          <Sekme
            etkin={sekme === 'rapor'}
            tikla={() => sekmeSecimiYaz('rapor')}
            etiket="Raporlar"
          />
        )}
      </div>

      {!yetkilerHazir ? (
        <p className="text-sm text-slate-500">Yetkiler yükleniyor…</p>
      ) : sekme === 'gunluk' && gorevGorebilir ? (
        <GunlukGorevler />
      ) : sekme === 'tip' && tipGorebilir ? (
        <GorevTipleri />
      ) : sekme === 'sablon' && sablonGorebilir ? (
        <GorevSablonlari />
      ) : raporGorebilir ? (
        <GorevRaporlari />
      ) : (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Bu ekranı görüntüleme yetkiniz yok.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Günlük görevler                                                     */
/* ------------------------------------------------------------------ */

function GunlukGorevler() {
  const sorguIstemcisi = useQueryClient();

  const acabilir = yetkiKullan('GOREV_OLUSTUR');

  const [birimId, birimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [tarih, tarihYaz] = useState(() => bugun());
  const [acikGorev, acikGorevYaz] = useState<GorevOzeti | null>(null);
  const [iptalEdilen, iptalEdilenYaz] = useState<GorevOzeti | null>(null);
  const [formAcik, formAcikYaz] = useState(false);

  /*
   * `birimId` SUNUCUDA ZORUNLU (`GunlukSorguDto` `@Matches`). Seçilmeden
   * istek gönderilseydi 400 dönerdi; ayrıca kapsam dışı birim 403 veriyor.
   */
  const gorevler = useQuery({
    queryKey: ['gorev', 'gunluk', { birimId, tarih }],
    queryFn: () => gunlukGorevler(birimId!, tarih),
    enabled: birimId !== undefined,
  });

  const iptal = useMutation({
    mutationFn: (girdi: { id: Kimlik; gerekce: string }) => gorevIptal(girdi.id, girdi.gerekce),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['gorev'] });
      iptalEdilenYaz(null);
    },
  });

  const sutunlar: Sutun<GorevOzeti>[] = [
    {
      baslik: 'Görev',
      hucre: (g) => (
        <span>
          <span className="block font-medium text-slate-900">{g.gorevTipiAd}</span>
          <span className="block text-xs text-slate-500">
            {g.gorevTipiKod}
            {g.lokasyonMetni !== null && ` · ${g.lokasyonMetni}`}
          </span>
        </span>
      ),
    },
    { baslik: 'Birim', hucre: (g) => g.birimAd },
    {
      baslik: 'Beklenen',
      hucre: (g) => (
        <span className="whitespace-nowrap text-xs">
          <span className="block">
            {g.beklenenSureDk === null ? '—' : `${g.beklenenSureDk} dk`}
            {g.beklenenPersonel !== null && ` × ${g.beklenenPersonel} kişi`}
          </span>
          <span className="block text-slate-500">
            {g.calismaBicimi === 'birlikte' ? 'birlikte' : 'dönüşümlü'}
          </span>
        </span>
      ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Şu an',
      // `acikKayitSayisi` = bu göreve ŞU AN kaç kişi çalışıyor.
      hucre: (g) =>
        g.acikKayitSayisi > 0 ? (
          <span className="whitespace-nowrap rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
            {g.acikKayitSayisi} kişi
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
      daralt: true,
    },
    { baslik: 'Durum', hucre: (g) => <GorevDurumRozeti durum={g.durum} />, daralt: true },
    {
      baslik: '',
      hucre: (g) => (
        <span className="flex justify-end gap-1 whitespace-nowrap">
          <button
            type="button"
            onClick={() => acikGorevYaz(g)}
            className="rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
          >
            Ayrıntı
          </button>
          {/* Başlamış veya bitmiş görev iptal edilmez. */}
          {acabilir && g.durum === 'planlandi' && (
            <button
              type="button"
              onClick={() => iptalEdilenYaz(g)}
              className="rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              İptal
            </button>
          )}
        </span>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-64">
            {/*
              "Tümü" seçeneği YOK: sunucu `birimId` istiyor ve kapsam dışı
              birimde 403 dönüyor. Boş seçenek konsaydı istek 400 alırdı.
            */}
            <BirimSecici deger={birimId} degisti={birimIdYaz} kendiBirimiyleBasla />
          </div>
          <Metin etiket="Tarih" tur="date" deger={tarih} degisti={tarihYaz} />
        </div>

        {acabilir && (
          <Dugme
            tikla={() => formAcikYaz(true)}
            pasif={birimId === undefined}
            cocuk="Plan dışı görev aç"
          />
        )}
      </div>

      <HataKutusu hata={gorevler.error} />

      {birimId === undefined ? (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Görevleri görmek için bir birim seçin.
        </p>
      ) : (
        <Tablo
          sutunlar={sutunlar}
          satirlar={gorevler.data ?? []}
          anahtar={(g) => g.gorevId}
          bekliyor={gorevler.isPending}
          bosMetin="Bu birim ve tarihte görev yok."
        />
      )}

      {acikGorev !== null && <GorevAyrintisi gorev={acikGorev} kapat={() => acikGorevYaz(null)} />}

      {iptalEdilen !== null && (
        <GerekceDiyalogu
          baslik={`${iptalEdilen.gorevTipiAd} görevini iptal et`}
          aciklama="Görev SİLİNMEZ, iptal edilir. Gerekçe kalıcı kayıttır."
          etiket="İptal gerekçesi"
          onayEtiketi="İptal et"
          onayTuru="tehlike"
          enCokKarakter={500}
          bekliyor={iptal.isPending}
          hata={iptal.error}
          kapat={() => iptalEdilenYaz(null)}
          onayla={(gerekce) => iptal.mutate({ id: iptalEdilen.gorevId, gerekce })}
        />
      )}

      {formAcik && birimId !== undefined && (
        <GorevAcDiyalogu birimId={birimId} tarih={tarih} kapat={() => formAcikYaz(false)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function GorevAyrintisi({ gorev, kapat }: { gorev: GorevOzeti; kapat: () => void }) {
  const guncel = useQuery({
    queryKey: ['gorev', 'ayrinti', gorev.gorevId],
    queryFn: () => import('../api/gorev').then((m) => m.gorevAyrintisi(gorev.gorevId)),
    // Listedeki satır yeterince bilgi taşıyor; ayrıntı yalnız tazeleme için.
    initialData: gorev,
  });

  const g = guncel.data;

  return (
    <Diyalog
      baslik={g.gorevTipiAd}
      aciklama={`${g.birimAd} · ${g.gorevTipiKod}`}
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <GorevDurumRozeti durum={g.durum} />
            <span className="text-xs text-slate-500">
              öncelik {g.oncelik} · kaynak {g.kaynak === 'sablon_otomatik' ? 'şablon' : 'elle'}
            </span>
          </div>

          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <Kutu
              etiket="Beklenen süre"
              deger={g.beklenenSureDk === null ? '—' : `${g.beklenenSureDk} dk`}
            />
            <Kutu
              etiket="Beklenen personel"
              deger={g.beklenenPersonel === null ? '—' : `${g.beklenenPersonel} kişi`}
            />
            <Kutu etiket="Çalışma biçimi" deger={CALISMA_ETIKETI[g.calismaBicimi]} />
          </dl>

          {g.lokasyonMetni !== null && <Kutu etiket="Lokasyon" deger={g.lokasyonMetni} />}

          {g.koordinat !== null && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <dt className="text-xs text-slate-500">Koordinat</dt>
              <dd className="mt-0.5">
                <code className="text-xs">
                  {g.koordinat.enlem.toFixed(6)}, {g.koordinat.boylam.toFixed(6)}
                </code>
              </dd>
            </div>
          )}

          {g.aciklama !== null && (
            <section className="rounded-md border border-slate-200 bg-white p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Açıklama
              </h3>
              <p className="mt-1 whitespace-pre-line text-sm text-slate-800">{g.aciklama}</p>
            </section>
          )}

          <div className="flex justify-end">
            <Dugme tur="ikincil" tikla={kapat} cocuk="Kapat" />
          </div>
        </div>
      }
    />
  );
}

function Kutu({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <dt className="text-xs text-slate-500">{etiket}</dt>
      <dd className="mt-0.5 font-medium text-slate-900">{deger}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function GorevAcDiyalogu({
  birimId,
  tarih,
  kapat,
}: {
  birimId: Kimlik;
  tarih: string;
  kapat: () => void;
}) {
  const sorguIstemcisi = useQueryClient();

  const [gorevTipiId, gorevTipiIdYaz] = useState<number | ''>('');
  const [lokasyonMetni, lokasyonMetniYaz] = useState('');
  const [koordinat, koordinatYaz] = useState<Koordinat | null>(null);
  const [beklenenSureDk, beklenenSureDkYaz] = useState('');
  const [beklenenPersonel, beklenenPersonelYaz] = useState('');
  const [oncelik, oncelikYaz] = useState('5');
  const [aciklama, aciklamaYaz] = useState('');

  const tipler = useQuery({
    queryKey: ['gorev', 'tip', birimId],
    queryFn: () => gorevTipleri(birimId),
  });

  const ac = useMutation({
    mutationFn: () =>
      gorevAc({
        gorevTipiId: Number(gorevTipiId),
        birimId,
        tarih,
        lokasyonMetni: lokasyonMetni.trim() === '' ? undefined : lokasyonMetni.trim(),
        koordinat,
        // Boş bırakılırsa görev tipindeki varsayılan devralınır.
        beklenenSureDk: beklenenSureDk === '' ? undefined : Number(beklenenSureDk),
        beklenenPersonel: beklenenPersonel === '' ? undefined : Number(beklenenPersonel),
        oncelik: Number(oncelik),
        aciklama: aciklama.trim() === '' ? undefined : aciklama.trim(),
      }),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['gorev'] });
      kapat();
    },
  });

  return (
    <Diyalog
      baslik="Plan dışı görev aç"
      aciklama={`İş emri · ${tarih}`}
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Görev tipi</span>
            <select
              value={gorevTipiId}
              onChange={(olay) =>
                gorevTipiIdYaz(olay.target.value === '' ? '' : Number(olay.target.value))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              <option value="">Seçin…</option>
              {/* Pasif tipler listelenmez; yeni görev onlara açılmamalı. */}
              {(tipler.data ?? [])
                .filter((t) => t.aktif)
                .map((t) => (
                  <option key={t.gorevTipiId} value={t.gorevTipiId}>
                    {t.ad}
                  </option>
                ))}
            </select>
          </label>

          <Metin
            etiket="Lokasyon (isteğe bağlı)"
            deger={lokasyonMetni}
            degisti={lokasyonMetniYaz}
          />

          <HaritaNoktasi deger={koordinat} degisti={koordinatYaz} />

          <div className="grid gap-4 sm:grid-cols-3">
            <Metin
              etiket="Beklenen süre (dk)"
              deger={beklenenSureDk}
              degisti={beklenenSureDkYaz}
              ipucu="Boş = tipten devralınır"
            />
            <Metin
              etiket="Beklenen personel"
              deger={beklenenPersonel}
              degisti={beklenenPersonelYaz}
            />
            <Metin
              etiket="Öncelik (1–9)"
              deger={oncelik}
              degisti={oncelikYaz}
              ipucu="1 en yüksek"
            />
          </div>

          <CokSatir
            etiket="Açıklama (isteğe bağlı)"
            deger={aciklama}
            degisti={aciklamaYaz}
            satir={2}
          />

          <HataKutusu hata={tipler.error} />
          <HataKutusu hata={ac.error} />

          <div className="flex justify-end gap-2">
            <Dugme tur="ikincil" tikla={kapat} pasif={ac.isPending} cocuk="Vazgeç" />
            <Dugme
              tikla={() => ac.mutate()}
              bekliyor={ac.isPending}
              pasif={gorevTipiId === ''}
              cocuk="Görevi aç"
            />
          </div>
        </div>
      }
    />
  );
}
